import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { logSystemEvent } from "@/services/system/logsService";
import { trackApiRequest } from "@/services/system/apiMonitoringService";
import { analytics } from "@/services/analytics";
import { secureRandomInt } from "@/utils/secureRandom";
import { scheduleSimulatedPageApiCalls } from "@/hooks/activityLoggerSimulationHelpers";

/**
 * Custom hook for logging page visits and user interactions with session replay integration.
 * @example
 * const { logUserAction, logFormSubmission } = useActivityLogger();
 * logUserAction('click', { button: 'submit' });
 * logFormSubmission('registration', true);
 * @param {none} - Takes no arguments, uses the location pathname and internal functions.
 * @returns {object} An object containing methods to log user actions and form submissions.
 * @description
 *   - Logs system events for navigation, user actions, and form submissions.
 *   - Integrates with session replay for user identification and tracking.
 *   - Simulates API activity based on the current page visited.
 *   - Logs form submissions and their corresponding API requests with success or failure status.
 *   - Utilizes randomization to simulate API request parameters such as method, status, and duration.
 */
export const useActivityLogger = () => {
  const location = useLocation();
  const shouldEmitSystemLogs = import.meta.env.DEV;
  const shouldSimulateApiActivity = import.meta.env.DEV;

  const { user } = useAuth();

  useEffect(() => {
    if (shouldEmitSystemLogs) {
      // Log page visits with user context (dev only; production would spam /api/system/logs)
      logSystemEvent({
        level: "info",
        component: "navigation",
        service: "frontend",
        message: `Page visited: ${location.pathname}`,
        context: {
          page: location.pathname,
          userId: user?.id,
          userEmail: user?.email,
          isAuthenticated: !!user,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        },
      });
    }

    // Track page view in session replay
    analytics.trackPageView(location.pathname, {
      userId: user?.id,
      email: user?.email,
      isAuthenticated: !!user,
      timestamp: new Date().toISOString(),
    });

    // Simulate some background API activity for the current page
    /**
     * Simulate API calls for current page based on location and logs the activity.
     * @example
     * simulateApiCalls()
     * No direct return value. Side effect: logs API requests.
     * @param {object} pageApiSimulation - An object representing the endpoints mapped to page paths.
     * @returns {void} No direct return value. Side effect: logs API requests.
     * @description
     *   - The function uses a timeout of 2 seconds before starting the API simulation process.
     *   - Each endpoint call is spaced by 1 second to simulate asynchronous API requests.
     *   - Randomly selects HTTP method and status to simulate different API responses.
     *   - Records request data include method, endpoint, status, duration, timestamp, and error information.
     */
    const pageApiSimulation = () => {
      if (!shouldSimulateApiActivity) {
        return;
      }
      // Skip simulation for blog pages (they have their own API calls)
      if (location.pathname.startsWith("/blog")) {
        return;
      }

      const pageEndpoints: { [key: string]: string[] } = {
        "/dashboard": ["/api/dashboard/stats"],
        "/transactions": ["/api/transactions/list", "/api/transactions/summary"],
        "/assets": ["/api/assets/list", "/api/assets/categories"],
        "/goals": ["/api/goals/list", "/api/goals/progress"],
        "/system": ["/api/system/logs", "/api/system/metrics", "/api/admin/users"],
        "/admin": ["/api/admin/stats", "/api/admin/users", "/api/admin/activity"],
        "/reports": ["/api/reports/data", "/api/reports/generate"],
        "/settings": ["/api/user/settings", "/api/user/preferences"],
      };

      const endpoints = pageEndpoints[location.pathname];

      // Skip if no endpoints defined for this page
      if (!endpoints) {
        return;
      }

      // Simulate 1-2 API calls for this page
      scheduleSimulatedPageApiCalls(endpoints);
    };

    pageApiSimulation();
  }, [location.pathname, shouldEmitSystemLogs, shouldSimulateApiActivity, user]);

  // Log user interactions with session replay integration
  /**
   * Logs user interaction activities with related context information.
   * @example
   * logUserAction('button-click', { buttonId: 'submit' })
   * // Logs an event with level 'info'
   * @param {string} action - The name or type of action performed by the user.
   * @param {any} [details] - Optional parameter containing additional information about the action.
   * @returns {void} No return value, as it logs an event.
   * @description
   *   - Incorporates contextual information such as the current page's pathname and a timestamp.
   *   - Uses 'logSystemEvent' to log the interaction details with predefined fields.
   *   - Sets the log level to 'info' and the component to 'user-interaction'.
   *   - Integrates with session replay for comprehensive user tracking.
   */
  const logUserAction = (action: string, details?: Record<string, unknown>) => {
    if (shouldEmitSystemLogs) {
      // Log to system (dev only)
      logSystemEvent({
        level: "info",
        component: "user-interaction",
        service: "frontend",
        message: `User action: ${action}`,
        context: {
          action,
          details,
          page: location.pathname,
          userId: user?.id,
          userEmail: user?.email,
          isAuthenticated: !!user,
          timestamp: new Date().toISOString(),
        },
      });
    }

    // Track in session replay
    analytics.trackUserAction(action, {
      ...details,
      page: location.pathname,
      userId: user?.id,
      email: user?.email,
      isAuthenticated: !!user,
    });
  };

  // Log form submissions with session replay integration
  /**
   * Logs the event and tracks the API request for a form submission
   * @example
   * logFormSubmission('Contact Us', false)
   * Logs an unsuccessful submission of the 'Contact Us' form
   * @param {string} formType - The type of the form being submitted.
   * @param {boolean} [success=true] - Indicates whether the form submission was successful.
   * @returns {void} Does not return anything.
   * @description
   *   - Logs detailed information about the form submission event, including component and service details.
   *   - Generates a message that indicates whether the form submission was successful or not, for both logging and tracking API requests.
   *   - The API request duration is simulated with a random value between 50 and 350 milliseconds.
   *   - Logs the API endpoint based on the form type, automatically converting it to lowercase and replacing spaces with dashes.
   *   - Integrates with session replay for comprehensive form tracking.
   */
  const logFormSubmission = (formType: string, success: boolean = true) => {
    const message = `Form submission: ${formType} - ${success ? "Success" : "Failed"}`;

    if (shouldEmitSystemLogs) {
      // Log to system (dev only)
      logSystemEvent({
        level: success ? "info" : "error",
        component: "form-submission",
        service: "frontend",
        message,
        context: {
          formType,
          success,
          page: location.pathname,
          userId: user?.id,
          userEmail: user?.email,
          isAuthenticated: !!user,
          timestamp: new Date().toISOString(),
        },
      });
    }

    // Track in session replay
    analytics.trackEvent("form_submission", {
      formType,
      success,
      page: location.pathname,
      userId: user?.id,
      email: user?.email,
      isAuthenticated: !!user,
      timestamp: new Date().toISOString(),
    });

    // Simulate API call for form submission
    const endpoint = `/api/${formType.toLowerCase().replace(/\s+/g, "-")}/submit`;
    const method = "POST";
    const status = success ? 200 : 400;
    const duration = secureRandomInt(300) + 50;

    // Track API call in session replay
    analytics.trackAPICall(endpoint, method, status, duration);

    trackApiRequest({
      method,
      endpoint,
      status,
      duration,
      timestamp: new Date().toISOString(),
      origin: "form-submission",
      error: success ? undefined : `Form submission failed for ${formType}`,
    });
  };

  return {
    logUserAction,
    logFormSubmission,
  };
};
